import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdCircuitGateCardComponent } from '../circuit-gates/hd-circuit-gate-card.component';
import { HdCircuitGateSectionComponent } from '../circuit-gates/hd-circuit-gate-section.component';
import {
  HD_LOVE_FALLBACK_COLOR,
  HD_LOVE_GATES_DISPLAY_ORDER,
  HD_LOVE_GATE_COLOR,
  HdLovePanelData,
} from './hd-love.metadata';

interface LoveGroupVm {
  readonly gates: string;
  readonly title: string;
  readonly lead: string;
  readonly paragraphs: readonly string[];
}

interface LovePanelVm {
  readonly title: string;
  /** Algemene cheat-sheet-tekst: kop, intro, Ra-quote en vervolg. */
  readonly heading: string;
  readonly paragraphs: readonly string[];
  readonly quote: string;
  readonly quoteAuthor: string;
  readonly paragraphs2: readonly string[];
  /** De twee poortgroepen van het poster (Vessel of Love / Alledaagse). */
  readonly groups: readonly LoveGroupVm[];
  readonly reflectionTitle: string;
  readonly reflectionQuestions: readonly string[];
  readonly quoteLeft: string;
  readonly quoteRight: string;
  readonly jumpHint: string;
}

/**
 * Love-gates-paneel (Connectiviteitspoorten): bovenaan de volledige algemene
 * cheat-sheet-tekst (intro + Ra-quote + de twee poortgroepen), daaronder de
 * love-blokjes (klikken springt naar de detailsectie), per poort een volledige
 * {@link HdCircuitGateSectionComponent}, en tot slot de reflectievragen en de twee
 * Ra-quotes van het poster. Geopend via `HdLovePanelService`; een `focusGate`
 * in de paneel-data laat het paneel na render automatisch naar de betreffende
 * poort scrollen (ook bij hergebruik van een al open paneel).
 */
@Component({
  selector: 'hd-love-gates-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdCircuitGateCardComponent, HdCircuitGateSectionComponent],
  templateUrl: './hd-love-gates-panel.component.html',
  styleUrl: './hd-love-gates-panel.component.scss',
})
export class HdLoveGatesPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdLovePanelData>
    | null;

  /** Auto-scroll naar de focus-poort, gedeeld met het voices-/memory-paneel. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly gates = HD_LOVE_GATES_DISPLAY_ORDER;

  protected readonly vm = computed<LovePanelVm>(() => ({
    title: this.text('panelTitle'),
    heading: this.text('general.heading'),
    paragraphs: this.list('general.paragraphs'),
    quote: this.text('general.quote'),
    quoteAuthor: this.text('general.quoteAuthor'),
    paragraphs2: this.list('general.paragraphs2'),
    groups: (['vessel', 'everyday'] as const).map(key => ({
      gates: this.text(`groups.${key}.gates`),
      title: this.text(`groups.${key}.title`),
      lead: this.text(`groups.${key}.lead`),
      paragraphs: this.list(`groups.${key}.paragraphs`),
    })),
    reflectionTitle: this.text('reflection.title'),
    reflectionQuestions: this.list('reflection.questions'),
    quoteLeft: this.text('quotes.left'),
    quoteRight: this.text('quotes.right'),
    jumpHint: this.text('jumpHint'),
  }));

  constructor() {
    // Beide content-talen eager laden: taalwissel + NL-fallback zonder flikker.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-poort wapent de
    // auto-scroll opnieuw; het scrollen zelf gebeurt in ngAfterViewChecked
    // zodra de sectie gerenderd is en de scope-tekst geladen is.
    effect(() => {
      const focusGate = this.panelData?.().focusGate ?? null;
      if (focusGate != null) {
        this.scroller.arm(
          `[data-section-gate="${focusGate}"]`,
          () => this.text(`gates.${focusGate}.keynote`) !== '',
        );
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  /** Railkleur van het blokje (en de sectie) van deze poort. */
  protected railFor(gate: number): string | undefined {
    return HD_LOVE_GATE_COLOR[gate] ?? HD_LOVE_FALLBACK_COLOR;
  }

  protected scrollToGate(gate: number): void {
    this.scroller.jump(`[data-section-gate="${gate}"]`);
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('love', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('love', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('love', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('love', key, 'nl');
  }
}
